import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { Mail, ShieldCheck, CheckCircle2, AlertTriangle, Wifi, Pencil, ExternalLink } from "lucide-react";

// AJUSTE DOS E-MAILS DA PLATAFORMA — área geral do Admin Master (item 6 do
// pedido). O perfil FINANCEIRO já é homologado dentro de Gestão Comercial →
// Configurações de cobrança (platform_email_settings) e continua vivendo lá
// intocado (zero risco de regressão); aqui ele só aparece como um resumo
// somente-leitura, com um botão que leva para a tela real de edição — nunca
// um segundo formulário duplicado. O perfil SISTEMA/SEGURANÇA (OTP de
// login, confirmação de e-mail, recuperação de senha, avisos de segurança)
// é inteiramente novo e vive só aqui.
const SMTP_SECURITY_LABEL = { ssl_tls: "SSL/TLS", starttls: "STARTTLS", none: "Sem TLS" };

export default function ConfiguracoesPlataforma() {
  const [billing, setBilling] = useState(null);
  const [system, setSystem] = useState(null);
  const [systemForm, setSystemForm] = useState({
    active: true, from_name: "Codexpy", from_email: "", reply_to: "",
    smtp_host: "", smtp_port: "587", security: "ssl_tls", smtp_username: "", smtp_password: "",
    security_authorization_email: "",
  });
  const [systemEditing, setSystemEditing] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [testTo, setTestTo] = useState("");

  const load = async () => {
    try {
      const [b, s] = await Promise.all([
        api.get("/admin/commercial/email-settings"),
        api.get("/admin/platform/system-email-settings"),
      ]);
      setBilling(b.data);
      setSystem(s.data);
      setSystemForm({
        active: s.data.active, from_name: s.data.from_name || "Codexpy", from_email: s.data.from_email || "",
        reply_to: s.data.reply_to || "",
        smtp_host: s.data.smtp_host || "", smtp_port: String(s.data.smtp_port || "587"),
        security: s.data.security || "ssl_tls", smtp_username: s.data.smtp_username || "", smtp_password: "",
        security_authorization_email: s.data.security_authorization_email || "",
      });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  // CORREÇÃO DO ENVIO DE OTP — a UI nunca mais oferece "conexão
  // compartilhada com o financeiro": a arquitetura SMTP real da Codexpy
  // usa DUAS mailboxes distintas (financeiro@ é alias de ramile@;
  // contato@ é alias de marcos@, senha própria, diferente da do
  // financeiro). Sempre envia use_shared_connection=false e sempre exige
  // a conexão SMTP dedicada do próprio perfil sistema — exatamente como o
  // financeiro já funciona.
  const saveSystem = async () => {
    try {
      await api.put("/admin/platform/system-email-settings", {
        active: systemForm.active, from_name: systemForm.from_name, from_email: systemForm.from_email,
        reply_to: systemForm.reply_to || null, use_shared_connection: false,
        smtp_host: systemForm.smtp_host, smtp_port: Number(systemForm.smtp_port) || 587,
        security: systemForm.security, smtp_username: systemForm.smtp_username,
        smtp_password: systemForm.smtp_password || null,
        security_authorization_email: systemForm.security_authorization_email || null,
      });
      toast.success("E-mail do sistema salvo");
      setSystemEditing(false);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const testConnection = async () => {
    setTestingConnection(true);
    try {
      const { data } = await api.post("/admin/platform/system-email-settings/test-connection");
      if (data.ok) toast.success(data.message || "Conexão bem-sucedida"); else toast.error(data.message || "Falha na conexão");
      await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setTestingConnection(false); }
  };
  const sendTest = async () => {
    if (!testTo) return toast.error("Informe um e-mail de destino");
    setSendingTest(true);
    try {
      const { data } = await api.post("/admin/platform/system-email-settings/send-test", { to: testTo });
      if (data.ok) toast.success(data.message || "E-mail de teste enviado"); else toast.error(data.message || "Falha ao enviar");
    } catch (e) { toast.error(formatApiError(e)); } finally { setSendingTest(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="font-heading font-bold text-xl text-slate-900">E-mails da plataforma</h1>
        <p className="text-sm text-slate-500 mt-1">
          Identidade e conexão SMTP usadas pela própria Codexpy — separadas por finalidade. Nunca confundir com o SMTP de
          cada empresa cliente (Tenant → Configurações → E-mail de cobrança), que é exclusivo dos clientes de cada tenant.
        </p>
      </div>

      {/* Card Financeiro — resumo somente-leitura; edição real permanece em
          Gestão Comercial → Configurações de cobrança (já homologado). */}
      <Card className="p-5 space-y-3" data-testid="plataforma-email-financeiro">
        <div className="flex items-center justify-between">
          <div className="font-heading font-semibold text-slate-900 flex items-center gap-2"><Mail size={16} /> E-mail financeiro</div>
          {billing?.password_configured ? <Badge className="bg-emerald-100 text-emerald-800">Configurado</Badge> : <Badge className="bg-slate-200 text-slate-700">Não configurado</Badge>}
        </div>
        <div className="text-sm bg-slate-50 border border-slate-200 rounded-md p-3 space-y-1">
          <div className="font-medium text-slate-900">{billing?.from_name || "-"} {billing?.from_email ? `<${billing.from_email}>` : ""}</div>
          <div className="text-slate-500">Uso: cobranças e mensalidades LOOTUS (boleto SaaS, PIX SaaS, lote)</div>
        </div>
        <Link to="/admin/comercial">
          <Button size="sm" variant="outline" data-testid="plataforma-email-financeiro-editar">
            <ExternalLink size={14} /> Configurar em Gestão Comercial
          </Button>
        </Link>
      </Card>

      {/* Card Sistema/Segurança — OTP de login, confirmação de e-mail,
          recuperação de senha, avisos de segurança. NUNCA cobrança. */}
      <Card className="p-5 space-y-3" data-testid="plataforma-email-sistema">
        <div className="flex items-center justify-between">
          <div className="font-heading font-semibold text-slate-900 flex items-center gap-2"><ShieldCheck size={16} /> E-mail do sistema</div>
          {system?.password_configured ? (
            <Badge className="bg-emerald-100 text-emerald-800">Configurado</Badge>
          ) : <Badge className="bg-slate-200 text-slate-700">Não configurado</Badge>}
        </div>
        <p className="text-sm text-slate-500">
          Uso: códigos de verificação (OTP) de login, confirmação de acesso e recuperação de senha. Nunca usado para
          cobrança — e o e-mail financeiro nunca é usado aqui.
        </p>

        {!systemEditing && (
          <div className="space-y-2">
            <div className="text-sm bg-slate-50 border border-slate-200 rounded-md p-3 space-y-1">
              <div><span className="text-slate-500">Remetente:</span>{" "}
                <span className="font-medium text-slate-900">{system?.from_name || "-"} {system?.from_email ? `<${system.from_email}>` : ""}</span>
              </div>
              <div><span className="text-slate-500">Reply-To:</span> <span className="font-medium text-slate-900">{system?.reply_to || "-"}</span></div>
              <div><span className="text-slate-500">SMTP:</span>{" "}
                <span className="font-medium text-slate-900">{system?.smtp_host ? `${system.smtp_host}:${system.smtp_port}` : "-"}</span>
              </div>
              <div><span className="text-slate-500">Segurança:</span> <span className="font-medium text-slate-900">{SMTP_SECURITY_LABEL[system?.security] || "-"}</span></div>
              <div><span className="text-slate-500">Usuário SMTP:</span> <span className="font-medium text-slate-900">{system?.smtp_username || "-"}</span></div>
              <div><span className="text-slate-500">Credencial:</span> <span className="font-medium text-slate-900">{system?.password_configured ? "Configurada" : "Não configurada"}</span></div>
              <div><span className="text-slate-500">E-mail de autorização de segurança:</span>{" "}
                <span className="font-medium text-slate-900">{system?.security_authorization_email || "Não configurado"}</span>
              </div>
              {system?.last_test_status && (
                <div className="flex items-center gap-1.5 pt-1">
                  {system.last_test_status === "success" ? <CheckCircle2 size={14} className="text-emerald-600" /> : <AlertTriangle size={14} className="text-red-600" />}
                  <span className="text-xs text-slate-500">
                    Último teste ({system.last_test_status === "success" ? "sucesso" : "falha"}
                    {system.last_test_at ? `, ${new Date(system.last_test_at).toLocaleString("pt-BR")}` : ""}): {system.last_test_detail}
                  </span>
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => setSystemEditing(true)} data-testid="plataforma-email-sistema-editar">
                <Pencil size={14} /> {system?.from_email ? "Editar" : "Configurar"}
              </Button>
              {system?.from_email && (
                <Button size="sm" variant="outline" disabled={testingConnection} onClick={testConnection} data-testid="plataforma-email-sistema-testar">
                  <Wifi size={14} /> {testingConnection ? "Testando..." : "Testar conexão"}
                </Button>
              )}
            </div>
            {system?.from_email && (
              <div className="flex flex-wrap items-end gap-2 pt-2 border-t border-slate-100">
                <div className="flex-1 min-w-[200px]">
                  <Label>Enviar e-mail de teste como Sistema para</Label>
                  <Input placeholder="seu-email@exemplo.com" value={testTo} onChange={e => setTestTo(e.target.value)} data-testid="plataforma-email-sistema-teste-destino" />
                </div>
                <Button size="sm" variant="outline" disabled={sendingTest} onClick={sendTest} data-testid="plataforma-email-sistema-enviar-teste">
                  <Mail size={14} /> {sendingTest ? "Enviando..." : "Enviar e-mail de teste"}
                </Button>
              </div>
            )}
          </div>
        )}

        {systemEditing && (
          <div className="space-y-3">
            <div className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Identidade</div>
            <div>
              <Label>Nome do remetente</Label>
              <Input value={systemForm.from_name} onChange={e => setSystemForm({ ...systemForm, from_name: e.target.value })} placeholder="Codexpy" data-testid="plataforma-email-sistema-from-name" />
            </div>
            <div>
              <Label>E-mail do remetente</Label>
              <Input value={systemForm.from_email} onChange={e => setSystemForm({ ...systemForm, from_email: e.target.value })} placeholder="contato@codexpy.com.br" data-testid="plataforma-email-sistema-from-email" />
            </div>
            <div>
              <Label>Reply-To</Label>
              <Input value={systemForm.reply_to} onChange={e => setSystemForm({ ...systemForm, reply_to: e.target.value })} placeholder="contato@codexpy.com.br" data-testid="plataforma-email-sistema-reply-to" />
            </div>

            <div className="text-xs uppercase tracking-wide text-slate-400 font-semibold pt-2">Conexão SMTP</div>
            <p className="text-xs text-slate-500 -mt-1">
              Conexão própria e dedicada deste perfil — nunca compartilhada com o e-mail financeiro (as mailboxes reais
              da Codexpy são diferentes, cada uma com sua própria senha).
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div className="col-span-2 sm:col-span-1">
                <Label>SMTP Host</Label>
                <Input value={systemForm.smtp_host} onChange={e => setSystemForm({ ...systemForm, smtp_host: e.target.value })} placeholder="smtp.hostinger.com" data-testid="plataforma-email-sistema-smtp-host" />
              </div>
              <div>
                <Label>SMTP Port</Label>
                <Input type="number" value={systemForm.smtp_port} onChange={e => setSystemForm({ ...systemForm, smtp_port: e.target.value })} placeholder="465" data-testid="plataforma-email-sistema-smtp-port" />
              </div>
            </div>
            <div>
              <Label>Segurança</Label>
              <Select value={systemForm.security} onValueChange={v => setSystemForm({ ...systemForm, security: v })}>
                <SelectTrigger data-testid="plataforma-email-sistema-security"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ssl_tls">SSL/TLS</SelectItem>
                  <SelectItem value="starttls">STARTTLS</SelectItem>
                  <SelectItem value="none">Sem TLS</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Usuário SMTP <span className="font-normal text-slate-400">(mailbox real — pode ser diferente do e-mail do remetente, se este for um alias)</span></Label>
              <Input value={systemForm.smtp_username} onChange={e => setSystemForm({ ...systemForm, smtp_username: e.target.value })} placeholder="marcos@codexpy.com.br" data-testid="plataforma-email-sistema-smtp-username" />
            </div>
            <div>
              <Label>Senha SMTP</Label>
              <Input type="password" value={systemForm.smtp_password} onChange={e => setSystemForm({ ...systemForm, smtp_password: e.target.value })}
                placeholder={system?.password_configured ? "Senha já configurada. Preencha somente para substituir." : "Digite a senha SMTP"} data-testid="plataforma-email-sistema-smtp-password" />
            </div>
            <div className="text-xs uppercase tracking-wide text-slate-400 font-semibold pt-2">Segurança</div>
            <div>
              <Label>E-mail de autorização de segurança</Label>
              <p className="text-xs text-slate-500 -mt-1 mb-1">
                Destinatário do código de autorização de ações administrativas sensíveis (ex.: redefinição de senha de
                administrador de empresa cliente). Sempre da própria Codexpy — nunca do cliente.
              </p>
              <Input
                value={systemForm.security_authorization_email}
                onChange={e => setSystemForm({ ...systemForm, security_authorization_email: e.target.value })}
                placeholder="marcos@codexpy.com.br" data-testid="plataforma-email-seguranca-autorizacao"
              />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={systemForm.active} onChange={e => setSystemForm({ ...systemForm, active: e.target.checked })} data-testid="plataforma-email-sistema-ativo" />
              Ativo
            </label>
            <div className="flex gap-2 pt-2">
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={saveSystem} data-testid="plataforma-email-sistema-salvar">Salvar</Button>
              <Button variant="outline" onClick={() => { setSystemEditing(false); load(); }} data-testid="plataforma-email-sistema-cancelar">Cancelar</Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
